import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SubitemTemplateService } from "../services/subitemTemplateService";
import { TemplateCategoryService } from "../services/templateCategoryService";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import { GripVertical, Plus, Hash, Trash2 } from "lucide-react";
import { Button, SearchBar, ConfirmDialog, MultiSelect } from "./ui";

// Sortable Template Row Component
const SortableTemplateRow = ({
  template,
  onUpdate,
  onDelete,
  onRightClick,
  categoryOptions,
  categoryMap,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: template.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "var(--dark-elevated)" : "var(--dark-bg)",
  };

  const [localTemplate, setLocalTemplate] = useState(template);
  const [saveTimeout, setSaveTimeout] = useState(null);

  // Update local state when prop changes
  useEffect(() => {
    setLocalTemplate(template);
  }, [template]);

  const handleChange = (field, value) => {
    const updated = { ...localTemplate, [field]: value };
    setLocalTemplate(updated);

    // Debounced save
    if (saveTimeout) clearTimeout(saveTimeout);
    const timeout = setTimeout(() => {
      onUpdate(template.id, updated);
    }, 500);
    setSaveTimeout(timeout);
  };

  const templateType = template.revisionType || "base";

  // Debug: Log options available to MultiSelect
  if (categoryOptions.length === 0) {
    console.log(
      "⚠️ SortableTemplateRow received empty categoryOptions for template:",
      template.id,
      template.name,
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onContextMenu={(e) => onRightClick(e, template)}
      className="template-row"
    >
      <div className="drag-handle" {...attributes} {...listeners}>
        <GripVertical size={18} />
      </div>

      <div className="template-content">
        <div className="template-field">
          <input
            type="text"
            value={localTemplate.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder={
              templateType === "revision"
                ? 'Set type to "Both" to edit base'
                : "Base subitem name"
            }
            disabled={templateType === "revision"}
            className="template-input"
          />
        </div>

        <div className="template-field">
          <input
            type="text"
            value={localTemplate.revisionVersion || ""}
            onChange={(e) => handleChange("revisionVersion", e.target.value)}
            placeholder={
              templateType === "base"
                ? 'Set type to "Both" or "Revision"'
                : "Revision subitem name"
            }
            disabled={templateType === "base"}
            className="template-input"
          />
        </div>

        <div className="template-field">
          <select
            value={templateType}
            onChange={(e) => {
              const newType = e.target.value;
              handleChange("revisionType", newType);
            }}
            className="template-select"
          >
            <option value="base">Base</option>
            <option value="revision">Revision</option>
            <option value="both">Both</option>
          </select>
        </div>

        <div className="template-field template-filters">
          <MultiSelect
            options={categoryOptions.map((opt) => opt.label)}
            selected={(localTemplate.categoryIds || [])
              .map(
                (id) => categoryMap[id]?.displayName || categoryMap[id]?.name,
              )
              .filter(Boolean)}
            onChange={(selectedNames) => {
              // Convert selected display names back to category IDs
              const selectedIds = selectedNames
                .map((name) => {
                  const cat = categoryOptions.find((opt) => opt.label === name);
                  return cat?.value;
                })
                .filter(Boolean);
              handleChange("categoryIds", selectedIds);
            }}
            placeholder="Select categories..."
            size="compact"
          />
        </div>

        <button
          className="delete-button"
          onClick={() => onDelete(template)}
          title="Delete template"
        >
          <Trash2 size={18} />
        </button>
      </div>

      <style jsx>{`
        .template-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-bottom: 1px solid var(--gray-800);
          margin-bottom: 0;
          transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease;
          background-color: transparent;
        }

        .template-row:hover {
          background-color: rgba(255, 255, 255, 0.03);
        }

        .drag-handle {
          display: flex;
          align-items: center;
          color: var(--gray-600);
          cursor: grab;
          padding: 0 4px;
          transition:
            color 0.15s ease,
            opacity 0.15s ease;
          opacity: 0.5;
        }

        .template-row:hover .drag-handle {
          opacity: 1;
          color: var(--gray-400);
        }

        .drag-handle:active {
          cursor: grabbing;
        }

        .template-content {
          flex: 1;
          display: grid;
          grid-template-columns: 2fr 2fr 120px 2fr 40px;
          gap: 8px;
          align-items: center;
        }

        .template-field {
          display: flex;
          align-items: center;
        }

        .template-input,
        .template-select {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid transparent;
          border-radius: 4px;
          font-size: 13px;
          background: transparent;
          color: var(--gray-100);
          transition: all 0.15s ease;
        }

        .template-input:hover:not(:disabled),
        .template-select:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: var(--gray-700);
        }

        .template-input:focus,
        .template-select:focus {
          outline: none;
          background: var(--dark-bg);
          border-color: var(--cta-blue);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
        }

        .template-input:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .delete-button {
          padding: 6px;
          background: none;
          border: none;
          color: var(--gray-600);
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
        }

        .template-row:hover .delete-button {
          opacity: 1;
        }

        .delete-button:hover {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }
      `}</style>
    </div>
  );
};

// Phase Section Component
const PhaseSection = ({
  phase,
  templates,
  onUpdate,
  onDelete,
  onDragEnd,
  onAutoNumber,
  onInsertTemplate,
  onRightClick,
  categoryOptions,
  categoryMap,
}) => {
  const templateIds = templates.map((t) => t.id);

  return (
    <div className="phase-section">
      <div className="phase-header">
        <div className="phase-info">
          <h3>{phase}</h3>
          <span className="phase-count">
            {templates.length} template{templates.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="phase-actions">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onInsertTemplate(phase)}
          >
            <Plus size={16} />
            Add Template
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onAutoNumber(phase)}>
            <Hash size={16} />
            Auto-Number
          </Button>
        </div>
      </div>

      <div className="phase-content">
        {templates.length === 0 ? (
          <div className="empty-phase">
            <p>No templates in this phase yet.</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onInsertTemplate(phase)}
            >
              <Plus size={16} />
              Add First Template
            </Button>
          </div>
        ) : (
          <SortableContext
            items={templateIds}
            strategy={verticalListSortingStrategy}
          >
            {templates.map((template) => (
              <SortableTemplateRow
                key={template.id}
                template={template}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onRightClick={onRightClick}
                categoryOptions={categoryOptions}
                categoryMap={categoryMap}
              />
            ))}
          </SortableContext>
        )}
      </div>

      <style jsx>{`
        .phase-section {
          margin-bottom: 24px;
          border: 1px solid var(--gray-800);
          border-radius: 8px;
          overflow: hidden;
          background: rgba(0, 0, 0, 0.2);
          animation: fadeIn 0.2s ease-in;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .phase-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: rgba(0, 0, 0, 0.3);
          border-bottom: 1px solid var(--gray-800);
        }

        .phase-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .phase-info h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: var(--gray-200);
          letter-spacing: 0.02em;
        }

        .phase-count {
          font-size: 12px;
          color: var(--gray-500);
          padding: 2px 8px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }

        .phase-actions {
          display: flex;
          gap: 6px;
        }

        .phase-content {
          padding: 0;
        }

        .empty-phase {
          padding: 32px 20px;
          text-align: center;
          color: var(--gray-500);
        }

        .empty-phase p {
          margin: 0 0 12px 0;
          font-size: 13px;
        }
      `}</style>
    </div>
  );
};

// Main Component
const SubitemTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    template: null,
  });
  const [contextMenu, setContextMenu] = useState(null);
  const [categories, setCategories] = useState([]);

  // Build filter options from database categories
  // Now uses category IDs and display names directly (no prefixes)
  const categoryOptions = useMemo(() => {
    console.log(
      "📋 Building categoryOptions from categories:",
      categories.length,
      "categories",
    );
    if (categories.length === 0) {
      console.log("⚠️ No categories loaded yet");
      return [];
    }

    // Group categories by type for better organization in dropdown
    const typeOrder = [
      "base",
      "project_type",
      "add_on",
      "file_format",
      "unit_system",
    ];

    const filtered = categories.filter(
      (cat) =>
        cat.categoryType !== "deliverable" || cat.name === "Linework Design",
    ); // Exclude Surface Design but keep Linework Design

    console.log("📋 After filter:", filtered.length, "categories");
    console.log("📋 Category types:", [
      ...new Set(filtered.map((c) => c.categoryType)),
    ]);

    return filtered
      .sort((a, b) => {
        const aTypeIndex = typeOrder.indexOf(a.categoryType);
        const bTypeIndex = typeOrder.indexOf(b.categoryType);
        if (aTypeIndex !== bTypeIndex) return aTypeIndex - bTypeIndex;
        return a.sortOrder - b.sortOrder;
      })
      .map((cat) => ({
        value: cat.id,
        label: cat.displayName || cat.name,
        categoryType: cat.categoryType,
      }));
  }, [categories]);

  // For backwards compatibility, also create a map of category ID to name
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      map[cat.id] = cat;
    });
    return map;
  }, [categories]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("🔄 Loading templates and categories from database...");

      // Load both templates and categories in parallel
      const [templatesData, categoriesData] = await Promise.all([
        SubitemTemplateService.getAllSubitemTemplates(),
        TemplateCategoryService.getCategories(),
      ]);

      console.log("✅ Templates loaded:", templatesData.length, "templates");
      console.log("✅ Categories loaded:", categoriesData.length, "categories");

      setTemplates(templatesData);
      setCategories(categoriesData);
    } catch (err) {
      console.error("Error loading templates:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const commonPhases = useMemo(
    () => [
      "00-Startup",
      "01-Data Prep",
      "02-Utilities",
      "03-Discrepancy Check",
      "04-Erosion/Pond",
      "05-Modeling",
      "06-Double Check",
      "07-Corrections",
      "08-Review",
      "09-Ready to Export",
    ],
    [],
  );

  const groupedTemplates = useMemo(() => {
    console.log("🔍 Filtering templates (in-memory, no database call)");
    let filtered = templates;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name?.toLowerCase().includes(search) ||
          t.revisionVersion?.toLowerCase().includes(search) ||
          t.templateFilters?.some((f) => f.toLowerCase().includes(search)),
      );
    }

    // Apply phase filter (multi-select)
    if (selectedPhases.length > 0) {
      filtered = filtered.filter((t) => selectedPhases.includes(t.phase));
    }

    // Group by phase
    const grouped = {};
    commonPhases.forEach((phase) => {
      grouped[phase] = filtered
        .filter((t) => t.phase === phase)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    console.log(
      "✨ Filtered to",
      Object.values(grouped).flat().length,
      "templates",
    );
    return grouped;
  }, [templates, searchTerm, selectedPhases, commonPhases]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    // Find which phase this belongs to
    let phase = null;
    for (const [p, temps] of Object.entries(groupedTemplates)) {
      if (temps.some((t) => t.id === active.id)) {
        phase = p;
        break;
      }
    }

    if (!phase) return;

    const phaseTemplates = groupedTemplates[phase];
    const oldIndex = phaseTemplates.findIndex((t) => t.id === active.id);
    const newIndex = phaseTemplates.findIndex((t) => t.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;
    if (oldIndex === newIndex) return; // Same position

    const draggedTemplate = phaseTemplates[oldIndex];

    // Use arrayMove to get the correct new arrangement
    const reordered = arrayMove(phaseTemplates, oldIndex, newIndex);

    // Calculate new order for the moved item based on its neighbors in new position
    let newOrder;
    const itemBefore = newIndex > 0 ? reordered[newIndex - 1] : null;
    const itemAfter =
      newIndex < reordered.length - 1 ? reordered[newIndex + 1] : null;

    if (!itemBefore) {
      // First position - place before the next item
      const nextOrder = itemAfter?.order || 10;
      newOrder = Math.max(1, Math.floor(nextOrder / 2));
    } else if (!itemAfter) {
      // Last position - place after the previous item
      newOrder = Math.ceil((itemBefore?.order || 0) + 10);
    } else {
      // Between two items - calculate midpoint
      const beforeOrder = itemBefore.order || 0;
      const afterOrder = itemAfter.order || beforeOrder + 20;

      // If orders are too close, we might need to renumber
      if (afterOrder - beforeOrder <= 1) {
        // Not enough space - renumber the entire phase
        console.log("Not enough space between items, renumbering phase");
        const updates = reordered.map((template, index) => ({
          id: template.id,
          order_number: (index + 1) * 10,
        }));

        // Update all items in this phase
        try {
          await SubitemTemplateService.batchUpdateOrderNumbers(updates);
          await loadTemplates();
        } catch (err) {
          console.error("Error renumbering phase:", err);
          setError(err.message);
          loadTemplates();
        }
        return;
      }

      // Calculate midpoint and round to avoid decimals
      newOrder = Math.floor((beforeOrder + afterOrder) / 2);

      // Ensure it's different from both neighbors
      if (newOrder === beforeOrder) newOrder = beforeOrder + 1;
      if (newOrder === afterOrder) newOrder = afterOrder - 1;
    }

    // Optimistic update: Update only the dragged item
    setTemplates((prevTemplates) => {
      return prevTemplates.map((template) => {
        if (template.id === draggedTemplate.id) {
          return { ...template, order: newOrder };
        }
        return template;
      });
    });

    // Update database in background (only the one item)
    try {
      await SubitemTemplateService.batchUpdateOrderNumbers([
        {
          id: draggedTemplate.id,
          order_number: newOrder,
        },
      ]);
    } catch (err) {
      // On error, reload to get correct state
      console.error("Error updating order number:", err);
      setError(err.message);
      loadTemplates();
    }
  };

  const handleUpdate = async (id, updated) => {
    try {
      console.log("⚡ Optimistic update (no reload):", updated);

      // Optimistic update - update local state immediately
      setTemplates((prevTemplates) =>
        prevTemplates.map((template) =>
          template.id === id ? { ...template, ...updated } : template,
        ),
      );

      // Update database in background
      await SubitemTemplateService.updateSubitemTemplate(id, updated);
      console.log("✅ Database updated silently");
    } catch (err) {
      console.error("Error updating template:", err);
      setError(err.message);
      // Reload on error to get correct state
      loadTemplates();
    }
  };

  const handleDelete = (template) => {
    setDeleteConfirm({ isOpen: true, template });
  };

  const confirmDelete = async () => {
    try {
      await SubitemTemplateService.deleteSubitemTemplate(
        deleteConfirm.template.id,
      );
      await loadTemplates();
      setDeleteConfirm({ isOpen: false, template: null });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAutoNumber = async (phase) => {
    const phaseTemplates = groupedTemplates[phase];
    const updates = phaseTemplates.map((template, index) => ({
      id: template.id,
      order_number: (index + 1) * 10,
    }));

    try {
      // Optimistic update - update local state immediately
      setTemplates((prevTemplates) =>
        prevTemplates.map((template) => {
          const update = updates.find((u) => u.id === template.id);
          if (update) {
            return { ...template, order: update.order_number };
          }
          return template;
        }),
      );

      // Update database in background
      await SubitemTemplateService.batchUpdateOrderNumbers(updates);
    } catch (err) {
      console.error("Error auto-numbering:", err);
      setError(err.message);
      // Reload on error
      loadTemplates();
    }
  };

  const handleInsertTemplate = async (phase, insertAfterOrder = null) => {
    const phaseTemplates = groupedTemplates[phase] || [];

    let newOrder = 1000;
    if (insertAfterOrder !== null && phaseTemplates.length > 0) {
      const index = phaseTemplates.findIndex(
        (t) => t.order === insertAfterOrder,
      );
      if (index !== -1) {
        const nextTemplate = phaseTemplates[index + 1];
        newOrder = nextTemplate
          ? Math.floor((insertAfterOrder + nextTemplate.order) / 2)
          : insertAfterOrder + 10;
      }
    } else if (phaseTemplates.length > 0) {
      newOrder = Math.max(...phaseTemplates.map((t) => t.order || 0)) + 10;
    }

    // Get all project type category IDs as default (what "Surface Design" used to mean)
    const projectTypeIds = categoryOptions
      .filter((opt) => opt.categoryType === "project_type")
      .map((opt) => opt.value);

    const newTemplate = {
      name: "New Template",
      phase: phase,
      order: newOrder,
      revisionVersion: "",
      revisionType: "base",
      categoryIds: projectTypeIds, // Default to all project types
    };

    try {
      const createdTemplate =
        await SubitemTemplateService.createSubitemTemplate(newTemplate);

      // Add to local state immediately (optimistic)
      if (createdTemplate) {
        setTemplates((prevTemplates) => [...prevTemplates, createdTemplate]);
      } else {
        // Fallback: reload if no template returned
        await loadTemplates();
      }

      setContextMenu(null);
    } catch (err) {
      console.error("Error creating template:", err);
      setError(err.message);
      // Reload on error
      loadTemplates();
    }
  };

  const handleRightClick = (e, template) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      phase: template.phase,
      insertAfterOrder: template.order,
    });
  };

  if (loading) {
    return <LoadingSpinner message="Loading templates..." />;
  }

  const activeTemplate = activeId
    ? templates.find((t) => t.id === activeId)
    : null;

  return (
    <div className="template-manager">
      {/* Header */}
      <div className="manager-header">
        <div>
          <h1>Subitem Template Manager</h1>
          <p>{templates.length} total templates</p>
        </div>
      </div>

      {error && (
        <ErrorMessage
          message={error}
          onRetry={loadTemplates}
          onDismiss={() => setError(null)}
        />
      )}

      {/* Filters */}
      <div className="filters">
        <SearchBar
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search templates..."
        />
        <div style={{ minWidth: "300px" }}>
          <MultiSelect
            options={commonPhases}
            selected={selectedPhases}
            onChange={setSelectedPhases}
            placeholder="Filter by phases..."
            size="md"
          />
        </div>
        {selectedPhases.length > 0 && (
          <div
            style={{
              fontSize: "13px",
              color: "var(--gray-500)",
              padding: "4px 12px",
              background: "rgba(59, 130, 246, 0.1)",
              borderRadius: "6px",
              border: "1px solid rgba(59, 130, 246, 0.2)",
            }}
          >
            Showing {selectedPhases.length} phase
            {selectedPhases.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Phase Sections */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="phases-container">
          {commonPhases.map((phase) => {
            const phaseTemplates = groupedTemplates[phase] || [];
            // Skip phases that don't match filter (if filter is active)
            if (selectedPhases.length > 0 && !selectedPhases.includes(phase))
              return null;
            // Skip empty phases when searching
            if (searchTerm && phaseTemplates.length === 0) return null;

            return (
              <PhaseSection
                key={phase}
                phase={phase}
                templates={phaseTemplates}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onAutoNumber={handleAutoNumber}
                onInsertTemplate={handleInsertTemplate}
                onRightClick={handleRightClick}
                categoryOptions={categoryOptions}
                categoryMap={categoryMap}
              />
            );
          })}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeTemplate ? (
            <div
              style={{
                padding: "8px 12px",
                background: "rgba(0, 0, 0, 0.95)",
                border: "1px solid var(--gray-700)",
                borderRadius: "4px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
                color: "var(--gray-100)",
                fontSize: "13px",
                fontWeight: 500,
                minWidth: "200px",
              }}
            >
              {activeTemplate.name || "New Template"}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() =>
              handleInsertTemplate(
                contextMenu.phase,
                contextMenu.insertAfterOrder,
              )
            }
          >
            <Plus size={16} />
            Insert Template Here
          </button>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, template: null })}
        onConfirm={confirmDelete}
        title="Delete Template"
        message={`Delete "${deleteConfirm.template?.name}"?`}
        confirmText="Delete"
        variant="danger"
      />

      <style jsx>{`
        .template-manager {
          padding: 20px 24px;
          background: var(--dark-bg);
          min-height: 100vh;
        }

        .manager-header {
          margin-bottom: 20px;
        }

        .manager-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: var(--gray-100);
          letter-spacing: -0.02em;
        }

        .manager-header p {
          margin: 6px 0 0 0;
          font-size: 13px;
          color: var(--gray-500);
        }

        .filters {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
          align-items: center;
        }

        .phases-container {
          max-width: 1400px;
        }

        .context-menu {
          background: var(--dark-elevated);
          border: 1px solid var(--gray-700);
          border-radius: 6px;
          padding: 4px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
        }

        .context-menu button {
          width: 100%;
          padding: 8px 12px;
          background: none;
          border: none;
          color: var(--gray-300);
          text-align: left;
          cursor: pointer;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          transition: all 0.15s ease;
        }

        .context-menu button:hover {
          background: rgba(59, 130, 246, 0.15);
          color: var(--cta-blue);
        }
      `}</style>
    </div>
  );
};

export default SubitemTemplateManager;
